import axios, { isAxiosError } from 'axios'
import { z } from 'zod'

import { tokenStorage } from './auth'
import { env } from '@/shared/config/env'

const refreshResponseSchema = z.object({
  access_token: z.string(),
  refresh_token: z.string(),
})

/**
 * Refresh outcomes mirror the mobile clients: only `rejected` (the server
 * definitively refused the refresh token with a 4xx) may drop the session.
 * Network errors, 5xx and malformed bodies are `unavailable` - the session
 * stays intact and the failed request simply errors.
 */
export type RefreshResult =
  | { outcome: 'success'; accessToken: string }
  | { outcome: 'rejected' }
  | { outcome: 'unavailable' }

/**
 * Single-flight refresh: when several requests hit 401 at once, only one
 * refresh call goes out and they all await the same promise.
 */
let refreshPromise: Promise<RefreshResult> | null = null

export function refreshSession(): Promise<RefreshResult> {
  refreshPromise ??= doRefresh().finally(() => {
    refreshPromise = null
  })
  return refreshPromise
}

async function doRefresh(): Promise<RefreshResult> {
  const refreshToken = tokenStorage.getRefreshToken()
  if (!refreshToken) return { outcome: 'rejected' }

  try {
    // Raw axios, not apiClient: the interceptors must not run for this call.
    const response = await axios.post<unknown>(
      `${env.apiBaseUrl}/api/eleva/auth/refresh`,
      { refresh_token: refreshToken },
      { timeout: env.apiTimeoutMs },
    )
    const parsed = refreshResponseSchema.safeParse(response.data)
    if (!parsed.success) return { outcome: 'unavailable' }

    tokenStorage.set({
      accessToken: parsed.data.access_token,
      refreshToken: parsed.data.refresh_token,
    })
    return { outcome: 'success', accessToken: parsed.data.access_token }
  } catch (error) {
    const status = isAxiosError(error) ? error.response?.status : undefined
    if (status !== undefined && status >= 400 && status < 500) return { outcome: 'rejected' }
    return { outcome: 'unavailable' }
  }
}
